import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Inbox, Loader2, Paperclip, Trash2, FileText, Search, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => `$${parseFloat(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Preview = ({ it }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u;
    axios.get(`${API}/receipts/unmatched/${it.id}/file`, { responseType: "blob" }).then(r => { u = URL.createObjectURL(r.data); setUrl(u); }).catch(() => {});
    return () => u && URL.revokeObjectURL(u);
  }, [it.id]);
  const open = () => url && window.open(url, "_blank");
  return (
    <button onClick={open} className="w-24 h-32 shrink-0 rounded-lg border border-stone-200 bg-stone-50 overflow-hidden flex items-center justify-center hover:border-stone-400 transition-colors" data-testid={`unmatched-preview-${it.id}`}>
      {it.content_type === "application/pdf" ? <FileText className="w-8 h-8 text-red-400" /> : url ? <img src={url} alt="receipt" className="w-full h-full object-cover" /> : <Loader2 className="w-4 h-4 animate-spin text-stone-300" />}
    </button>
  );
};

const TxnOption = ({ t, onPick, busy, testId }) => (
  <div className="flex items-center gap-3 px-3 py-1.5 text-xs border-t border-stone-100 first:border-t-0" data-testid={testId}>
    <span className="text-stone-500 w-20">{t.date}</span>
    <span className="flex-1 truncate text-stone-800">{t.vendor || t.description || t.category}</span>
    <span className="font-medium">{fmt(t.amount)}</span>
    <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" disabled={busy} onClick={() => onPick(t)}><Paperclip className="w-3 h-3 mr-1" /> Attach</Button>
  </div>
);

const UnmatchedCard = ({ it, transactions, onDone }) => {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const ex = it.extracted || {};
  const results = q.trim().length < 2 ? [] : transactions.filter(t => !t.locked && !(t.receipts?.length) &&
    `${t.vendor || ""} ${t.description || ""} ${t.amount} ${t.date}`.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 8);

  const attach = async (t) => {
    setBusy(true);
    try { await axios.post(`${API}/receipts/unmatched/${it.id}/attach`, { txn_id: t.id }); toast.success(`Receipt attached to ${t.vendor || t.description || "transaction"}`); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not attach"); setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try { await axios.delete(`${API}/receipts/unmatched/${it.id}`); toast.success("Receipt discarded"); onDone(); }
    catch (e) { toast.error("Could not delete"); setBusy(false); }
  };

  return (
    <Card className="border-stone-200/60" data-testid={`unmatched-${it.id}`}>
      <CardContent className="p-4 flex gap-4">
        <Preview it={it} />
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-stone-900" data-testid={`unmatched-read-${it.id}`}>{ex.vendor || "Unknown vendor"} · {ex.total ? fmt(ex.total) : "no total"} · {ex.date || "no date"}</p>
              <p className="text-xs text-stone-500">{ex.memo}{it.caption && <span className="italic"> · “{it.caption}”</span>}</p>
              <p className="text-[11px] text-stone-400 mt-0.5 flex items-center gap-1"><Send className="w-3 h-3" /> {it.uploaded_by} · {new Date(it.created_at).toLocaleString()}</p>
            </div>
            {confirmDel ? (
              <span className="flex items-center gap-1">
                <Button size="sm" className="h-7 px-2 text-xs bg-red-600 hover:bg-red-700 text-white" disabled={busy} onClick={remove} data-testid={`unmatched-delete-confirm-${it.id}`}>Discard</Button>
                <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setConfirmDel(false)} data-testid={`unmatched-delete-cancel-${it.id}`}>Cancel</Button>
              </span>
            ) : <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600 hover:bg-red-50" disabled={busy} onClick={() => setConfirmDel(true)} data-testid={`unmatched-delete-${it.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
          </div>
          {it.candidates?.length > 0 && (
            <div className="border border-stone-100 rounded-lg" data-testid={`unmatched-candidates-${it.id}`}>
              <p className="text-[10px] uppercase tracking-wide text-stone-500 px-3 pt-2 pb-1">Possible matches</p>
              {it.candidates.map(t => <TxnOption key={t.id} t={t} onPick={attach} busy={busy} testId={`unmatched-candidate-${it.id}-${t.id}`} />)}
            </div>
          )}
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2 text-stone-400" />
            <Input className="h-7 pl-8 text-xs" placeholder="Search any transaction by provider, memo, amount or date" value={q} onChange={e => setQ(e.target.value)} data-testid={`unmatched-search-${it.id}`} />
          </div>
          {results.length > 0 && <div className="border border-stone-100 rounded-lg">{results.map(t => <TxnOption key={t.id} t={t} onPick={attach} busy={busy} testId={`unmatched-result-${it.id}-${t.id}`} />)}</div>}
        </div>
      </CardContent>
    </Card>
  );
};

export const UnmatchedReceipts = ({ onChanged }) => {
  const [items, setItems] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const load = () => {
    axios.get(`${API}/receipts/unmatched`).then(r => setItems(r.data)).catch(() => setItems([]));
    axios.get(`${API}/transactions`).then(r => setTransactions(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const done = () => { load(); onChanged(); };
  return (
    <div className="space-y-4" data-testid="unmatched-receipts-tab">
      <Card className="border-stone-200/60">
        <CardContent className="p-6">
          <h3 className="text-base font-semibold text-stone-900 flex items-center gap-2"><Inbox className="w-5 h-5 text-stone-500" /> Unmatched Receipts
            {items?.length > 0 && <span className="text-xs font-medium text-stone-500 bg-stone-100 rounded-full px-2 py-0.5">{items.length}</span>}</h3>
          <p className="text-sm text-stone-500 mt-1">Send a receipt photo or PDF to the Telegram bot. It's attached automatically when exactly one transaction matches the amount (±3 days); otherwise it waits here.</p>
        </CardContent>
      </Card>
      {items === null ? <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin text-stone-400 mx-auto" /></div>
        : items.length === 0 ? <p className="text-sm text-stone-400 text-center py-10" data-testid="unmatched-empty">No unmatched receipts</p>
        : items.map(it => <UnmatchedCard key={it.id} it={it} transactions={transactions} onDone={done} />)}
    </div>
  );
};
